import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, FormGroup, Label } from 'reactstrap';
import { Post, Discard } from 'Constant';
import { Btn, H5, H6, Spinner } from 'AbstractElements';
import TryoutForm from './TryoutForm';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { submitTryout } from 'Features/Learning/Tryout/Utils/TryoutUtils';
import { resetState, setField } from 'Features/Learning/Tryout/addTryoutSlice';
import DropzoneCommon from 'Component/Learning/Common/Upload/Dropzone';
import AsyncSelectCommon from 'Component/Learning/Common/SelectOption/AsyncSelect';
import VideoPlayer from 'Component/Learning/DetailSubMateri/VideoPlayer';
import { fetchCategories } from 'Features/Learning/Categories/Utils/CategoriesUtils';
import Swal from 'sweetalert2';

const TryoutPostContain = () => {
  const dispatch = useDispatch();
  const history = useNavigate();
  const data = useSelector(state => state.addTryout)
  const [selectedValue, setSelectedValue] = useState(null);
  const filtersCategory = useSelector(state => state.Categories.filters)
  const startUpload = useSelector(state => state.addTryout.isLoading);

  useEffect(() => {
    const selectedCategory = data.category && data.category.id
      ? [{
        value: data.category.id,
        label: data.category.title,
      }]
      : selectedValue;
    setSelectedValue(selectedCategory);
  }, [data.id]);

  const handleSuccess = () => {
    dispatch(resetState());
    history(`${process.env.PUBLIC_URL}/learning/tryout-list`);
  };

  const showError = (errorMessage) => {
    Swal.fire({
      icon: 'error',
      title: 'Oops...',
      text: errorMessage,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const isEmptyDescription = (desc) => !desc || desc === '<p><br></p>';
    const isMissing = (val) => val === '' || val === null || typeof val === 'undefined';
    const isEmptyRichText = (val) => !val || val.trim() === '' || val === '<p></p>' || val === '<p><br></p>';

    // Build validations
    const validations = [
      { cond: isMissing(data.title), msg: 'Judul belum diisi.' },
      { cond: isMissing(data.is_quiz), msg: 'Jenis Tryout belum dipilih.' },
      { cond: isMissing(data.is_toefl), msg: 'Tipe Tryout belum dipilih.' },
      { cond: isMissing(data.is_live), msg: 'Tipe live belum dipilih.' },
      { cond: isMissing(data.time), msg: 'Durasi belum diisi.' },
      { cond: isMissing(data.start_at), msg: 'Waktu Mulai belum diisi.' },
      { cond: isMissing(data.end_at), msg: 'Waktu Berakhir belum diisi.' },
      { cond: isMissing(data.quotas), msg: 'Kuota Peserta belum diisi.' },
      { cond: isEmptyDescription(data.description), msg: 'Deskripsi belum diisi.' },
      { cond: isMissing(data.category), msg: 'Kategori belum dipilih.' },
    ];

    // Validasi Lock Tryout Feature
    if (data.locked_type && data.locked_type !== 'none') {
      validations.push({ cond: isMissing(data.locked_until_at), msg: 'Waktu Unlock belum diisi.' });
    }

    // Banner required only on create; on update check existing link or new banner
    if (!data.isUpdate) {
      validations.push({ cond: isMissing(data.banner), msg: 'Banner belum dipilih.' });
    } else {
      const needsBanner = isMissing(data.banner) && isMissing(data.bannerLink);
      if (needsBanner) validations.push({ cond: true, msg: 'Banner belum dipilih.' });
    }

    const failed = validations.find(v => v.cond);
    if (failed) {
      showError(failed.msg);
      return;
    }

    // Validasi toefl_setup jika tryout adalah TOEFL
    if (data.is_toefl == '1') {
      if (!Array.isArray(data.toefl_setup) || data.toefl_setup.length < 2) {
        showError('TOEFL Setup harus memiliki minimal 2 stage.');
        return;
      }
      for (let i = 0; i < data.toefl_setup.length; i++) {
        const stage = data.toefl_setup[i];
        const stageNum = i + 1;
        if (isMissing(stage.name)) {
          showError(`Nama stage ${stageNum} pada TOEFL Setup belum diisi.`);
          return;
        }
        if (isMissing(stage.total_questions)) {
          showError(`Jumlah soal stage ${stageNum} pada TOEFL Setup belum diisi.`);
          return;
        }
        if (isMissing(stage.duration)) {
          showError(`Durasi stage ${stageNum} pada TOEFL Setup belum diisi.`);
          return;
        }
      }
    }

    // Prepare data: ensure lock fields are never null
    const submitData = { ...data };

    // Ensure locked_message tidak null (convert ke empty string)
    if (submitData.locked_message === null || submitData.locked_message === undefined) {
      submitData.locked_message = '';
    }

    // Jika locked_type = 'none', reset lock fields ke empty string
    if (!submitData.locked_type || submitData.locked_type === 'none') {
      submitData.locked_type = 'none';
      submitData.locked_until_at = '';
      submitData.locked_message = '';
    }

    // All validations passed -> submit
    dispatch(submitTryout({ ...submitData, successCallback: handleSuccess }));
  };

  const handleDiscard = (e) => {
    e.preventDefault();
    dispatch(resetState());
    history(`${process.env.PUBLIC_URL}/learning/tryout-list`);
  };

  const handleChangeBanner = ({ meta, file }, status) => {
    if (status === 'removed') {
      dispatch(setField({ field: 'banner', value: '' }));
    } else if (status === 'done') {
      dispatch(setField({ field: 'banner', value: file }));
    }
  };

  const handleChangeAds = ({ meta, file }, status) => {
    if (status === 'removed') {
      dispatch(setField({ field: 'ads_video', value: '' }));
    } else if (status === 'done') {
      dispatch(setField({ field: 'ads_video', value: file }));
    }
  };

  const handleChangeTutorial = ({ meta, file }, status) => {
    if (status === 'removed') {
      dispatch(setField({ field: 'tutorial_video', value: '' }));
    } else if (status === 'done') {
      dispatch(setField({ field: 'tutorial_video', value: file }));
    }
  };

  const promiseOptions = (inputValue, callback) => {
    dispatch(fetchCategories({
      ...filtersCategory,
      'filter[is_parent]': '1',
      'filter[title]': inputValue,
    }))
      .then((response) => {
        const options = response.payload.data.map(item => ({
          value: item.id,
          label: item.title,
        }));
        callback(options);
      })
      .catch((error) => {
        console.error('Error fetching options:', error);
        callback([]);
      });
  };

  const handleSelectChange = (selectedOption) => {
    setSelectedValue(selectedOption);
    if (selectedOption === null) {
      dispatch(setField({ field: 'category', value: '' }));
      setSelectedValue(null);
    } else {
      dispatch(setField({ field: 'category', value: selectedOption.value }));
      setSelectedValue(selectedOption);
    }
  };

  const handleDeleteFile = (e, type) => {
    e.preventDefault();
    Swal.fire({
      title: 'Apakah anda yakin?',
      text: `Anda tidak dapat mengembalikan ${type === "tutorial_video" ? 'Tutorial Video' : 'Video iklan'} yang telah dihapus!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33'
    }).then(async (result) => {
      if (result.isConfirmed) {
        dispatch(setField({ field: type, value: 'delete' }));
        if (type === "tutorial_video") {
          dispatch(setField({ field: 'tutorial_videoLink', value: '' }));
        } else if (type === "ads_video") {
          dispatch(setField({ field: 'ads_videoLink', value: '' }));
        }
      }
    })
  }

  return (
    <Container fluid={true}>
      {startUpload ? (
        <Col lg="12">
          <div className="loader-box">
            <Spinner attrSpinner={{ className: 'loader-30' }} />
          </div>
          <H6 attrH6={{ className: 'sub-title mb-0 text-center' }}>{'Sedang proses unggah...'}</H6>
        </Col>
      ) : (
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader className='pb-0'><H5>{data.isUpdate ? '' : 'Tryout Baru'}</H5></CardHeader>
              <CardBody className="add-post">
                <TryoutForm />
                <DropzoneCommon label={'Banner Tryout'} inputContent={data.bannerLink ? 'Banner tryout tersedia' : 'Silahkan upload gambar.'} handleChangeStatus={handleChangeBanner} accept="image/*" />
                <AsyncSelectCommon label={'Pilih Kategori'} placeholder={'Silahkan pilih Kategori...'}
                  isMulti={false}
                  promiseOptions={promiseOptions}
                  handleSelectChange={handleSelectChange}
                  selectedValue={selectedValue}
                  isDisabled={data.is_have_questions}
                />
                {(data.isUpdate === true && data.tutorial_videoLink) && (
                  <FormGroup className='mb-3'>
                    <Label for="validationImage" style={{ fontWeight: 'bold', marginBottom: '15px', marginRight: '10px' }}>Preview Video Tutorial:</Label>
                    <Btn attrBtn={{ className: 'btn btn-danger', onClick: (e) => handleDeleteFile(e, "tutorial_video") }}>
                      <i className="fa fa-trash fa-lg"></i>
                    </Btn>
                    <VideoPlayer link={data.tutorial_videoLink} width={720} />
                  </FormGroup>
                )}
                <DropzoneCommon label={'Video Tutorial'} inputContent={data.tutorial_videoLink ? 'video tutorial tersedia' : 'Silahkan upload video tutorial'} handleChangeStatus={handleChangeTutorial} accept=".mp4" />
                {(data.isUpdate === true && data.ads_videoLink) && (
                  <FormGroup className='mb-3'>
                    <Label for="validationImage" style={{ fontWeight: 'bold', marginBottom: '15px', marginRight: '10px' }}>Preview Video Iklan:</Label>
                    <Btn attrBtn={{ className: 'btn btn-danger', onClick: (e) => handleDeleteFile(e, "ads_video") }}>
                      <i className="fa fa-trash fa-lg"></i>
                    </Btn>
                    <VideoPlayer link={data.ads_videoLink} width={720} />
                  </FormGroup>
                )}
                <DropzoneCommon label={'Video Iklan'} inputContent={data.ads_videoLink ? 'video iklan tersedia' : 'Silahkan upload video iklan'} handleChangeStatus={handleChangeAds} accept=".mp4" />
                <div className="btn-showcase text-end">
                  <Btn attrBtn={{ color: 'primary', type: 'submit', onClick: handleSubmit }}>{Post}</Btn>
                  <Btn attrBtn={{ color: 'light', type: 'reset', onClick: handleDiscard }}>{Discard}</Btn>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default TryoutPostContain;